import { ExercisePhase } from '@pelckmans/business-components/components/exercise-manager/exercise-types';
import { useContext } from 'react';
import { ValuesOfObject } from '../../../../../@types/utils';
import { type MyAssignment } from '../../../../../api/assignment';
import { getMedialinkById } from '../../../../../api/medialink';
import { Module } from '../../../../../api/module';
import { getMaterialByAssignment } from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../contexts/ModalContext';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import ModuleContext from '../../../_contexts/ModuleContext';
import type { FinishedStudentAssignment } from '../../../assignments/student/api/finished-student-assignment';
import useMedialinks from '../medialink/useMedialinks';
import useUserMaterial from '../user-material/useUserMaterial';

// Hook for handling student assignments
export default function useAssignmentHandling({
  onAssignmentClosed,
  subLocationId,
  assignmentModule,
}: {
  onAssignmentClosed: () => Promise<void>;
  subLocationId: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>;
  assignmentModule: Module;
}) {
  const { module } = useContext(ModuleContext);
  const { showModal } = useContext(ModalContext);

  const { openMedialink, openReview } = useMedialinks();
  const { openMaterial } = useUserMaterial();
  const handleError = useThrowAsyncError();

  async function openAssignment(assignment: MyAssignment, phase?: ExercisePhase) {
    try {
      if (assignment.materialType === 'medialink') {
        const medialink = await getMedialinkById(assignmentModule.id, assignment.materialId, {
          includeAllGroups: true,
          includeSubModules: true,
        });

        const node = medialink.hierarchy[medialink.hierarchy.length - 1];
        const modal = await openMedialink(medialink, node, assignmentModule, onAssignmentClosed, phase, subLocationId);

        if (modal) showModal(modal);

        return;
      }

      const usm = await getMaterialByAssignment(assignment);

      const modal = await openMaterial(usm, usm.hierarchy, onAssignmentClosed, subLocationId);

      if (modal) showModal(modal);
    } catch (err) {
      handleError(err);
    }
  }

  async function reviewAssignment(assignment: FinishedStudentAssignment) {
    if (!module) return;

    if (assignment.materialType === 'medialink') {
      if (!assignment.sessionId) return;

      const medialink = await getMedialinkById(module.id, assignment.materialId, {
        includeAllGroups: true,
        includeSubModules: true,
        includePastAssignments: true,
      });

      medialink.assignments = [
        {
          ...assignment,
          current: true,
        },
      ];
      const node = medialink.hierarchy[medialink.hierarchy.length - 1];

      const modal = await openReview({
        medialink,
        selectedModule: module,
        sessionId: assignment.sessionId,
        subLocationId,
        nodeWithoutDescription: node,
        session: {
          duration: assignment.score.duration,
          score: assignment.score,
          status: assignment.score.status,
          phases: assignment.score.phases,
          isLastPhase: assignment.score.isLastPhase,
        },
      });

      if (modal) showModal(modal);

      return;
    }

    const usm = await getMaterialByAssignment(assignment);

    const modal = await openMaterial(usm, usm.hierarchy, undefined, subLocationId);

    if (modal) showModal(modal);
  }

  return {
    startAssignment: (assignment: MyAssignment) => openAssignment(assignment, 'start'),
    restartAssignment: (assignment: MyAssignment) => openAssignment(assignment, 'start'),
    resumeAssignment: (assignment: MyAssignment) => openAssignment(assignment, 'resume'),
    reviewAssignment: (assignment: FinishedStudentAssignment) => reviewAssignment(assignment),
  };
}
